import { KNOWN_EVENT_TYPES, type RawFrame, type StreamEvent } from "./contract";

/**
 * Incremental parser for the contract's SSE framing (section 3):
 *
 *     event: <type>\n
 *     data: <single-line JSON>\n
 *     \n
 *
 * Fed arbitrary string chunks, so a line may be split anywhere. Lines end in
 * `\n` and `\r\n` is tolerated. A line starting with `:` is a comment
 * (keep-alive) and is ignored, as is any line that is neither `event:` nor
 * `data:`. One optional space after the colon is stripped.
 *
 * A frame whose `data` is not valid JSON is dropped. The JSON error is
 * deliberately not kept or rethrown: its message quotes the offending text, and
 * the text of an answer must never reach an error report.
 */
export class SseParser {
  private buffer = "";
  private type: string | null = null;
  private started = false;

  /** Consume a decoded chunk and return the frames it completed. */
  push(chunk: string): RawFrame[] {
    this.buffer += chunk;
    if (!this.started && this.buffer.length > 0) {
      this.started = true;
      if (this.buffer.charCodeAt(0) === 0xfeff) {
        this.buffer = this.buffer.slice(1);
      }
    }

    const frames: RawFrame[] = [];
    let newline: number;
    while ((newline = this.buffer.indexOf("\n")) >= 0) {
      const line = this.buffer.slice(0, newline).replace(/\r$/, "");
      this.buffer = this.buffer.slice(newline + 1);
      const frame = this.line(line);
      if (frame) frames.push(frame);
    }
    return frames;
  }

  /** The stream ended: handle a final line that had no trailing newline. */
  end(): RawFrame[] {
    const rest = this.buffer.replace(/\r$/, "");
    this.buffer = "";
    if (rest === "") return [];
    const frame = this.line(rest);
    return frame ? [frame] : [];
  }

  private line(line: string): RawFrame | null {
    if (line === "") {
      this.type = null;
      return null;
    }
    if (line.startsWith(":")) return null;

    if (line.startsWith("event:")) {
      this.type = stripOneSpace(line.slice(6)).trim();
      return null;
    }
    if (line.startsWith("data:") && this.type) {
      try {
        return {
          type: this.type,
          data: JSON.parse(stripOneSpace(line.slice(5)))
        };
      } catch {
        return null;
      }
    }
    return null;
  }
}

function stripOneSpace(value: string): string {
  return value.startsWith(" ") ? value.slice(1) : value;
}

/**
 * Read a response body as raw frames. Multi-byte characters split across chunks
 * are reassembled by the streaming decoder.
 */
export async function* readFrames(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<RawFrame> {
  const reader = body.getReader();
  const decoder = new TextDecoder("utf-8");
  const parser = new SseParser();
  let drained = false;
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      yield* parser.push(decoder.decode(value, { stream: true }));
    }
    drained = true;
    yield* parser.push(decoder.decode());
    yield* parser.end();
  } finally {
    // A consumer that stops early (a terminal frame, an abort, a thrown error)
    // must tell the source to stop, or the connection keeps streaming into a
    // reader nobody reads. Cancelling a stream that already errored or closed
    // can reject, which there is nothing to do about.
    if (!drained) reader.cancel().catch(() => undefined);
    reader.releaseLock();
  }
}

/** Whether a frame is an event this client knows. Unknown types are ignored
 *  (contract section 3), which keeps older clients working with new events. */
export function isKnownEvent(frame: RawFrame): frame is StreamEvent {
  return (
    KNOWN_EVENT_TYPES.has(frame.type) &&
    frame.data !== null &&
    typeof frame.data === "object" &&
    !Array.isArray(frame.data)
  );
}

/** Read a response body as the events this client understands. */
export async function* readEvents(
  body: ReadableStream<Uint8Array>
): AsyncGenerator<StreamEvent> {
  for await (const frame of readFrames(body)) {
    if (isKnownEvent(frame)) yield frame;
  }
}
